import { Injectable } from "@angular/core"
import { BehaviorSubject, Observable, of, Subject } from "rxjs"
import { throwError as observableThrowError } from "rxjs/internal/observable/throwError"
import { catchError, map } from "rxjs/operators"
import {
    InternalMediaSlide,
    LessonBuilderItem,
    LessonBuilderResource,
} from "../models/lessonBuilderItem.model"
import { LessonBuilderListItem } from "../models/lessonBuilderListItem.model"
import { Paginated } from "../models/paginated.model"
import { LmApiService } from "./lm-api.service"
import { SearchingService } from "./searching.service"
import { LearningObject } from "../models/learningObject.model"
import { MediaSlideComponent } from "../tools/lesson-builder/lesson-builder/media-slide/media-slide.component"
import { HttpClient } from "@angular/common/http"
import { Asset } from "../models/resource/asset"
import { Resource } from "../models/resource/resource"
import { UserService } from "./user.service"

export const defaultEditorSettings = {
    toolbar:
        "undo redo | formatselect | backcolor | bold italic underline | \
        hr removeformat | \
        alignleft aligncenter alignright alignjustify | \
        bullist numlist outdent indent table link ",
    block_formats: "Paragraph=p; Title=h5; Subtitle=h6;",
    plugins: "hr lists table link",
}

export interface LessonBuilderSearchData {
    learningObjects: LearningObject[]
    total: number
    prevUri: string | null
    nextUri: string | null
    facetSelection: FacetSelectionType
}

export interface FacetSelectionType {
    text: string
    resourceType: string
    gradeType: string
    showUnavailableResources: boolean
}

@Injectable()
export class LessonBuilderService {
    public mediaSlideSource: MediaSlideComponent

    private addExtraMediaSlide = new Subject<InternalMediaSlide>()
    public readonly addExtraMediaSlide$ = this.addExtraMediaSlide.asObservable()

    private mediaSelectDrawerOpen = new BehaviorSubject<boolean>(false)
    public readonly isMediaSelectDrawerOpen$ = this.mediaSelectDrawerOpen.asObservable()

    private searchResults = new BehaviorSubject<LessonBuilderSearchData | {}>({})
    public readonly searchResults$ = this.searchResults.asObservable()

    private searchFavoritesResults = new BehaviorSubject<LessonBuilderSearchData | {}>({})
    public readonly searchFavoritesResults$ = this.searchFavoritesResults.asObservable()

    private isAnySearchLoading = new BehaviorSubject<boolean>(false)
    public readonly isAnySearchLoading$ = this.isAnySearchLoading.asObservable()

    private _checkQuestionsForErrors = new BehaviorSubject<Boolean>(false)
    public readonly checkQuestionsForErrors$ = this._checkQuestionsForErrors.asObservable()

    public prefilledResource = new BehaviorSubject<Resource | null>(null)

    constructor(
        private lmApiService: LmApiService,
        private searchingService: SearchingService,
        private http: HttpClient,
        private userService: UserService
    ) {}

    public checkQuestionsForErrors(hasErrors: boolean) {
        this._checkQuestionsForErrors.next(hasErrors)
    }

    public openMediaSelectDrawer(mediaSlideSource: MediaSlideComponent) {
        this.mediaSlideSource = mediaSlideSource
        this.mediaSelectDrawerOpen.next(true)
    }

    public closeMediaSelectDrawer() {
        this.mediaSlideSource = null
        this.mediaSelectDrawerOpen.next(false)
    }

    public getLessons(limit: number, offset: number): Observable<Paginated<LessonBuilderListItem>> {
        const params = limit !== undefined && offset !== undefined ? { limit, offset } : {}
        return this.lmApiService
            .get("/api/v2/lesson-builder/", { responseType: "json", params })
            .pipe(
                map((response: Paginated<any>) => {
                    response.results = response.results.map((d) =>
                        LessonBuilderListItem.fromData(d)
                    )
                    return response
                }),
                catchError((error) => observableThrowError(error))
            )
    }

    public getLesson(id: string) {
        return this.lmApiService
            .get(`/api/v2/lesson-builder/${id}/`, { responseType: "json" })
            .pipe(
                map((response: any) => {
                    return { lessonBuilder: new LessonBuilderItem(response), error: null }
                }),
                catchError((error) => {
                    return of({ lessonBuilder: null, error: error })
                })
            )
    }

    public saveLesson(lesson: LessonBuilderItem) {
        const request = lesson.code
            ? this.lmApiService.put(`/api/v2/lesson-builder/${lesson.code}/`, lesson.toData())
            : this.lmApiService.post(`/api/v2/lesson-builder/`, lesson.toData())
        return request.pipe(
            map((response: any) => {
                return new LessonBuilderItem(response)
            }),
            catchError((error) => observableThrowError(error))
        )
    }

    public delete(id: string) {
        return this.lmApiService.delete(`/api/v2/lesson-builder/${id}/`)
    }

    public static makeDefaultFacetSelection(): FacetSelectionType {
        return {
            text: "",
            resourceType: "All",
            gradeType: "All",
            showUnavailableResources: false,
        }
    }

    public searchFavorites(facetSelection: FacetSelectionType): void {
        const {
            text,
            resourceType: media_type,
            gradeType: grades,
            showUnavailableResources,
        } = facetSelection
        const facets = []
        this.isAnySearchLoading.next(true)

        if (!showUnavailableResources) facets.push("scope:lesson")
        if (media_type !== "All") facets.push(`media_type:${media_type}`)
        if (grades !== "All") facets.push(`grades:${grades}`)

        this.searchingService
            .getFavorites({
                q: text,
                limit: "4",
                object_type: "resource",
                selected_facet: [...facets],
            })
            .subscribe(
                (res: any) => {
                    const { count: total, next: nextUri, previous: prevUri } = res
                    const learningObjects = res.results.map((obj) => new LearningObject(obj))
                    this.searchFavoritesResults.next({
                        learningObjects,
                        total,
                        prevUri,
                        nextUri,
                        facetSelection,
                    })
                    this.isAnySearchLoading.next(false)
                },
                (error) => console.error(`[CUSTOM Error]${error.message}`)
            )
    }

    /**
     * Used for getting data by using the URL,
     * for example when using pagination and we are getting the next and prev URL from keys like (next_uri, prev_uri)
     * @param url - string with all the data needed (facets, filters, limit, etc...)
     * @param facetSelection user selected filters
     */
    public searchByURL(url: string, facetSelection: FacetSelectionType): void {
        if (!url) return
        this.isAnySearchLoading.next(true)
        this.lmApiService.get(url).subscribe(
            (data: any) => {
                let learningObjects, total, prevUri, nextUri
                if (url.includes("/search-favorites/")) {
                    total = data.count
                    prevUri = data.previous || null
                    nextUri = data.next || null
                    learningObjects = data.results.map((obj) => new LearningObject(obj))
                    this.searchFavoritesResults.next({
                        learningObjects,
                        total,
                        prevUri,
                        nextUri,
                        facetSelection,
                    })
                    this.isAnySearchLoading.next(false)
                } else {
                    total = data.meta.total
                    prevUri = data.meta.prev_uri || null
                    nextUri = data.meta.next_uri || null
                    learningObjects = data.objects.map((obj) => new LearningObject(obj))
                    this.searchResults.next({
                        learningObjects,
                        total,
                        prevUri,
                        nextUri,
                        facetSelection,
                    })
                    this.isAnySearchLoading.next(false)
                }
            },
            (error) => console.error(`[CUSTOM Error]${error.message}`)
        )
    }

    public search(facetSelection: FacetSelectionType): void {
        let { text, resourceType: media_type, gradeType: grades } = facetSelection
        const facets = []
        this.isAnySearchLoading.next(true)

        // Even if no media filter is selected, we want to exclude Lesson Plans
        if (media_type !== "All") facets.push(`media_type:${media_type}`)
        if (grades !== "All") facets.push(`grades:${grades}`)

        this.searchingService
            .getResults({
                q: text,
                count: "4",
                object_type: "resource",
                ...(media_type === "All" && { excluded_facet: "Lesson%20Plan" }),
                selected_facet: [
                    // Always search for resources that are available in tools thus keep scope:lesson
                    "scope:lesson",
                    ...facets,
                ],
            })
            .subscribe(
                (data: any) => {
                    const {
                        meta: { total, next_uri: nextUri, prev_uri: prevUri },
                    } = data

                    const learningObjects = data.objects.map((obj) => new LearningObject(obj))
                    this.searchResults.next({
                        learningObjects,
                        total,
                        prevUri,
                        nextUri,
                        facetSelection,
                    })
                    this.isAnySearchLoading.next(false)
                },
                (error) => console.error(`[CUSTOM Error]${error.message}`)
            )
    }

    public addToSlide(learningObject: LearningObject) {
        return this.getResource(
            learningObject.resourceCode
        ).subscribe((resource: LessonBuilderResource) =>
            this.setResourceInSelectedMediaSlide(resource)
        )
    }

    public setResourceInSelectedMediaSlide(resource: LessonBuilderResource) {
        if (this.mediaSlideSource) {
            this.mediaSlideSource.setSelectedInternalContent(
                resource.assets[0],
                resource.guid,
                resource.brand,
                resource.creditsLink
            )
        }
        for (let i = 1; i < resource.assets.length; i++) {
            this.addExtraMediaSlide.next(
                new InternalMediaSlide(
                    null,
                    resource.assets[i].title,
                    "",
                    resource.assets[i],
                    resource.brand,
                    resource.creditsLink,
                    resource.guid
                )
            )
        }
        this.closeMediaSelectDrawer()
    }

    public addResourceToLessonBuilder(resource: Resource) {
        resource.assets.forEach((asset) => {
            this.addExtraMediaSlide.next(
                new InternalMediaSlide(
                    null,
                    resource.title,
                    resource.description,
                    asset,
                    resource.brand.name,
                    `/credits/${this.userService.addStudentInUrl(resource.resourceCode)}/`,
                    resource.guid
                )
            )
        })
    }

    public previewForSlide(resourceCode): Observable<LessonBuilderResource> {
        return this.getResource(resourceCode)
    }

    public getResource(resourceCode): Observable<LessonBuilderResource> {
        const resourceURL = `/api/v2/resource/${resourceCode}/`
        return this.lmApiService
            .getWithCookiesInParams(resourceURL, ["user_content_permission", "student"])
            .pipe(map((response: any) => LessonBuilderResource.fromData(response)))
            .pipe(catchError((error) => this.lmApiService.handleErrorForNonCritical(error)))
    }

    public uploadFile(file: File): Observable<any> {
        if (!file) return
        const formData: FormData = new FormData()
        formData.append("images", file)

        return this.http.post("/api/v2/ugc/", formData, {
            reportProgress: true,
            observe: "events",
            responseType: "json",
        })
    }

    public addUGCToSlide(ugcData: any) {
        const asset = Asset.fromData(ugcData.data)
        this.mediaSlideSource.setSelectedUGCContent(asset, ugcData.guid, null)
        this.closeMediaSelectDrawer()
    }

    public static flattenErrors(errors: Record<string, any>, keyPrefix = null): [string, string][] {
        return Object.entries(errors).reduce((acc, [key, val]) => {
            let fullKey = keyPrefix ? `${keyPrefix} - ${key}` : key
            if (key === "non_field_errors") {
                fullKey = "Error"
            }
            val.forEach((err) => {
                if (typeof err === "string") {
                    acc.push([fullKey, err])
                } else {
                    acc = [...acc, ...LessonBuilderService.flattenErrors(err, fullKey)]
                }
            })
            return acc
        }, [])
    }
}
